import { motion, useReducedMotion } from 'framer-motion'
import { AudioLines, Globe, GraduationCap, MousePointerClick, ShieldCheck, type LucideIcon } from 'lucide-react'
import { trustSignals } from '@/lib/site'
import { cn } from '@/lib/utils'

const icons: Record<(typeof trustSignals)[number]['icon'], LucideIcon> = {
  research: GraduationCap,
  interviews: AudioLines,
  global: Globe,
  privacy: ShieldCheck,
  setup: MousePointerClick,
}

interface TrustBarProps {
  className?: string
  delay?: number
  /** Glass panel of its own (default); off when the bar sits inside another card. */
  framed?: boolean
}

/**
 * Short credibility signals about VoxDiscover itself, under a hero and in closing sections.
 * Divider colours carry `!`: index.css sets an unlayered `* { border-color }` (kept identical to
 * the portal) that otherwise beats every border-colour utility.
 */
export function TrustBar({ className, delay = 0, framed = true }: TrustBarProps) {
  const reduceMotion = useReducedMotion()

  return (
    <motion.ul
      initial={reduceMotion ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      aria-label="Why teams trust VoxDiscover"
      className={cn(
        'flex flex-wrap justify-center gap-y-5',
        framed &&
          'rounded-2xl border border-black/10 bg-white/45 px-2 py-5 backdrop-blur-xl dark:border-white/[0.08] dark:bg-white/[0.06]',
        className
      )}
    >
      {trustSignals.map((signal) => {
        const Icon = icons[signal.icon]
        return (
          <li
            key={signal.lead}
            className="flex basis-1/2 flex-col items-center gap-2 px-2 text-center sm:basis-1/3 lg:basis-1/5 lg:border-l lg:border-foreground/10! lg:first:border-l-0"
          >
            <span
              aria-hidden
              className="flex size-10 items-center justify-center rounded-xl bg-linear-to-br from-cyan-500/20 via-blue-500/20 to-purple-500/20 ring-1 ring-blue-500/20 dark:from-cyan-400/15 dark:via-blue-400/15 dark:to-purple-400/15"
            >
              <Icon className="size-5 text-blue-700 dark:text-blue-300" />
            </span>
            <span>
              <span className="block text-base font-bold tracking-tight text-foreground sm:text-lg">
                {signal.lead}
              </span>
              <span className="block text-sm text-muted-foreground">{signal.rest}</span>
            </span>
          </li>
        )
      })}
    </motion.ul>
  )
}
